import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import CircularLoading from "../components/CircularLoading";
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import CountryListSelect from '../components/CountryListSelect';
import Button from '@mui/material/Button';
import { PanicSettings, BookingImageSettings } from 'common/sharedFunctions';
import { DispatchSettings } from 'common/sharedFunctions';
import SaveIcon from '@mui/icons-material/Save';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecurityIcon from '@mui/icons-material/Security';
import TuneIcon from '@mui/icons-material/Tune';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PublicIcon from '@mui/icons-material/Public';
import SendIcon from '@mui/icons-material/Send';
import PaletteIcon from '@mui/icons-material/Palette';

const ADMIN_THEME_PRESETS = [
  {
    key: 'slate',
    label: 'Slate',
    light: { primary: '#0f172a', accent: '#38bdf8', background: '#f8fafc', paper: '#ffffff' },
    dark: { primary: '#38bdf8', accent: '#22d3ee', background: '#0b1120', paper: '#111827' },
  },
  {
    key: 'amber',
    label: 'Amber',
    light: { primary: '#b45309', accent: '#f59e0b', background: '#fffbeb', paper: '#ffffff' },
    dark: { primary: '#fbbf24', accent: '#f97316', background: '#1f1307', paper: '#2a1c0f' },
  },
  {
    key: 'emerald',
    label: 'Emerald',
    light: { primary: '#047857', accent: '#10b981', background: '#f0fdf4', paper: '#ffffff' },
    dark: { primary: '#34d399', accent: '#84cc16', background: '#062e1f', paper: '#0b3b2b' },
  },
];

const useStyles = makeStyles(theme => ({
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#F9FAFB',
      '&:hover': {
        backgroundColor: '#F3F4F6',
      },
      '&.Mui-focused': {
        backgroundColor: '#FFFFFF',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E5E7EB',
    },
    '& .MuiInputLabel-root': {
      color: '#6B7280',
    },
  },
  rootRtl: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 20
    },
    "& legend": {
      textAlign: "right",
      marginRight: 20
    }
  },
  switchLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      color: '#374151',
    },
  },
}));

// Reusable Card Component
const SettingsCard = ({ icon, iconBgColor, iconColor, title, subtitle, children }) => (
  <Box
    sx={{
      bgcolor: '#FFFFFF',
      borderRadius: 3,
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      height: '100%',
    }}
  >
    <Box
      sx={{
        p: 2.5,
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: iconBgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 20 } })}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 16 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {children}
    </Box>
  </Box>
);

// Modern Switch Component
const ModernSwitch = ({ checked, onChange, name, label, isRTL }) => (
  <FormControlLabel
    sx={{
      m: 0,
      py: 1,
      px: 1.5,
      borderRadius: 2,
      bgcolor: '#F9FAFB',
      flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row',
      justifyContent: 'space-between',
      width: '100%',
      '&:hover': {
        bgcolor: '#F3F4F6',
      },
      '& .MuiFormControlLabel-label': {
        fontSize: 14,
        color: '#374151',
        fontWeight: 500,
      },
    }}
    control={
      <Switch
        checked={checked}
        onChange={onChange}
        name={name}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#111827',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#111827',
          },
        }}
      />
    }
    label={label}
    labelPlacement={isRTL === 'rtl' ? 'start' : 'end'}
  />
);

const GeneralSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editSettings,
    clearSettingsViewError,
    countries
  } = api;
  const settingsdata = useSelector(state => state.settingsdata);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [data, setData] = useState();
  const [clicked, setClicked] = useState(false);
  const [countryCode, setCountryCode] = useState();
  const [country, setCountry] = useState(false);

  useEffect(() => {
    if (data) {
      for (let i = 0; i < countries.length; i++) {
        if (countries[i].label === data.country) {
          setCountryCode(countries[i]);
        }
        if (countries[i].code === data.restrictCountry) {
          setCountry(countries[i]);
        }
      }
    }
  }, [data, countries]);


  useEffect(() => {
    if (settingsdata.settings) {
      const adminTheme = settingsdata.settings.adminTheme || {};
      setData({
        maintenanceMode: false,
        maintenanceMessage: '',
        adminTheme: {
          preset: adminTheme.preset || 'slate',
          mode: adminTheme.mode || 'light',
          ...adminTheme,
        },
        ...settingsdata.settings
      });
    }
  }, [settingsdata.settings]);

  const handleSwitchChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.checked });
  };

  const handleTextChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleAdminThemeChange = (patch) => {
    setData((prev) => ({
      ...prev,
      adminTheme: {
        preset: prev?.adminTheme?.preset || 'slate',
        mode: prev?.adminTheme?.mode || 'light',
        ...(prev?.adminTheme || {}),
        ...(patch || {}),
      },
    }));
  };

  const adminTheme = data?.adminTheme || { preset: 'slate', mode: 'light' };
  const adminPreset = ADMIN_THEME_PRESETS.find((p) => p.key === adminTheme.preset) || ADMIN_THEME_PRESETS[0];
  const adminSwatch = adminPreset ? adminPreset[adminTheme.mode === 'dark' ? 'dark' : 'light'] : null;

  const handleCountryChange = (value) => {
    setData({ ...data, country: value.label });
  };

  const handleCountryCode = (value) => {
    setData({ ...data, restrictCountry: value.code });
  };

  const handleBonusChange = (e) => {
    setData({ ...data, bonus: parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : '' });
  };

  const handleDecimalChange = (e) => {
    setData({ ...data, decimal: parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : '' });
  };

  const handleDriverRadius = (e) => {
    setData({ ...data, driverRadius: parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : '' });
  };

  const handleNumberSetting = (name) => (e) => {
    const v = e.target.value;
    const n = parseInt(v, 10);
    setData({ ...data, [name]: isNaN(n) ? '' : n });
  };

  const handleWalletMoney = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    if (data.AllowCriticalEditsAdmin) {
      if (data.bonus === '') {
        alert(t('proper_bonus'))
      } else {
        setClicked(true);
        dispatch(editSettings(data));
      }
    } else {
      alert(t('demo_mode'));
    }
  }

  const handleClose = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };


  return (
    data ?
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('general_settings')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('configure_app_behavior') || 'Configure your application behavior and preferences'}
          </Typography>
        </Box>

        <Grid container spacing={3} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
          {/* Currency Settings Card */}
          <Grid item xs={12} md={6} lg={4}>
            <SettingsCard
              icon={<AttachMoneyIcon />}
              iconBgColor="#DCFCE7"
              iconColor="#16A34A"
              title={t('currency_settings')}
              subtitle={t('currency_config') || 'Currency and payment settings'}
            >
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="symbol"
                label={t('currency_symbol')}
                name="symbol"
                onChange={handleTextChange}
                value={data.symbol}
              />
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="code"
                label={t('currency_code')}
                name="code"
                onChange={handleTextChange}
                value={data.code}
              />
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="decimal"
                label={t('set_decimal')}
                name="decimal"
                onChange={handleDecimalChange}
                value={data.decimal}
              />
              <ModernSwitch
                checked={data.swipe_symbol}
                onChange={handleSwitchChange}
                name="swipe_symbol"
                label={t('swipe_symbol')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.disable_online}
                onChange={handleSwitchChange}
                name="disable_online"
                label={t('disable_online')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.disable_cash}
                onChange={handleSwitchChange}
                name="disable_cash"
                label={t('disable_cash')}
                isRTL={isRTL}
              />
            </SettingsCard>
          </Grid>

          {/* Security Settings Card */}
          <Grid item xs={12} md={6} lg={4}>
            <SettingsCard
              icon={<SecurityIcon />}
              iconBgColor="#FEE2E2"
              iconColor="#DC2626"
              title={t('security_title')}
              subtitle={t('security_config') || 'Security and approval settings'}
            >
              <ModernSwitch
                checked={data.otp_secure}
                onChange={handleSwitchChange}
                name="otp_secure"
                label={t('settings_label3')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.driver_approval}
                onChange={handleSwitchChange}
                name="driver_approval"
                label={t('settings_label4')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.carApproval}
                onChange={handleSwitchChange}
                name="carApproval"
                label={t('settings_label5')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.imageIdApproval}
                onChange={handleSwitchChange}
                name="imageIdApproval"
                label={t('settings_label6')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.allow_password_login}
                onChange={handleSwitchChange}
                name="allow_password_login"
                label={t('allow_password_login') || 'Allow Password Login'}
                isRTL={isRTL}
              />
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="bonus"
                label={t('referral_bonus')}
                name="bonus"
                onChange={handleBonusChange}
                value={data.bonus}
              />
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="walletMoneyField"
                label={t('wallet_money_field')}
                name="walletMoneyField"
                onChange={handleWalletMoney}
                value={data.walletMoneyField}
              />
            </SettingsCard>
          </Grid>

          {/* Dispatch Settings Card */}
          <Grid item xs={12} md={6} lg={4}>
            <SettingsCard
              icon={<SendIcon />}
              iconBgColor="#EEF2FF"
              iconColor="#6366F1"
              title={t('dispatch_settings') || 'Dispatch Settings'}
              subtitle={t('dispatch_config') || 'Configure ride dispatch behavior'}
            >
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="preDispatchWindowMins"
                label={t('pre_dispatch_window_mins') || 'Pre-dispatch window (mins)'}
                name="preDispatchWindowMins"
                value={data.preDispatchWindowMins || ''}
                onChange={handleNumberSetting('preDispatchWindowMins')}
              />
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="requestTimeoutSec"
                label={t('request_timeout_sec') || 'Request timeout (sec)'}
                name="requestTimeoutSec"
                value={data.requestTimeoutSec || ''}
                onChange={handleNumberSetting('requestTimeoutSec')}
              />
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', mb: 0.5, display: 'block' }}>
                  {t('dispatch_mode') || 'Dispatch mode'}
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={data.dispatchMode || 'nearest'}
                  onChange={(e) => setData({ ...data, dispatchMode: e.target.value })}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#F9FAFB',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                  }}
                >
                  <MenuItem value={'nearest'}>{t('mode_nearest') || 'Nearest'}</MenuItem>
                  <MenuItem value={'online_longest'}>{t('mode_online_longest') || 'Online longest'}</MenuItem>
                  <MenuItem value={'hybrid'}>{t('mode_hybrid') || 'Nearest & online longest'}</MenuItem>
                </Select>
              </Box>
              <DispatchSettings
                autoDispatch={data.autoDispatch}
                onChange={handleSwitchChange}
              />
            </SettingsCard>
          </Grid>

          {/* Driver Settings Card */}
          <Grid item xs={12} md={6} lg={4}>
            <SettingsCard
              icon={<DirectionsCarIcon />}
              iconBgColor="#FEF3C7"
              iconColor="#D97706"
              title={t('driver_setting')}
              subtitle={t('driver_config') || 'Driver behavior settings'}
            >
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                id="driverRadius"
                label={t('driverRadius')}
                name="driverRadius"
                onChange={handleDriverRadius}
                value={data.driverRadius}
              />
              <ModernSwitch
                checked={data.negativeBalance}
                onChange={handleSwitchChange}
                name="negativeBalance"
                label={t('negative_balance')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.bank_fields}
                onChange={handleSwitchChange}
                name="bank_fields"
                label={t('bank_fields')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.showLiveRoute}
                onChange={handleSwitchChange}
                name="showLiveRoute"
                label={t('show_live_route')}
                isRTL={isRTL}
              />
              <PanicSettings
                classes={classes}
                data={data}
                handleTextChange={handleTextChange}
              />
            </SettingsCard>
          </Grid>

          {/* Advanced Settings Card */}
          <Grid item xs={12} md={6} lg={4}>
            <SettingsCard
              icon={<TuneIcon />}
              iconBgColor="#F3E8FF"
              iconColor="#9333EA"
              title={t('advance_settings')}
              subtitle={t('advanced_config') || 'Advanced app configurations'}
            >
              <ModernSwitch
                checked={data.AllowCountrySelection}
                onChange={handleSwitchChange}
                name="AllowCountrySelection"
                label={t('allow_multi_country')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.convert_to_mile}
                onChange={handleSwitchChange}
                name="convert_to_mile"
                label={t('convert_to_mile')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.CarHornRepeat}
                onChange={handleSwitchChange}
                name="CarHornRepeat"
                label={t('car_horn_repeat')}
                isRTL={isRTL}
              />
              <BookingImageSettings
                data={data}
                handleSwitchChange={handleSwitchChange}
              />
              <ModernSwitch
                checked={data.RiderWithDraw}
                onChange={handleSwitchChange}
                name="RiderWithDraw"
                label={t('rider_withdraw')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.horizontal_view}
                onChange={handleSwitchChange}
                name="horizontal_view"
                label={t('car_view_horizontal')}
                isRTL={isRTL}
              />
              <ModernSwitch
                checked={data.useDistanceMatrix}
                onChange={handleSwitchChange}
                name="useDistanceMatrix"
                label={t('use_distance_matrix')}
                isRTL={isRTL}
              />
            </SettingsCard>
          </Grid>

          {/* Admin Theme Card */}
          <Grid item xs={12} md={6} lg={4}>
            <SettingsCard
              icon={<PaletteIcon />}
              iconBgColor="#E0F2FE"
              iconColor="#0284C7"
              title={t('admin_theme') || 'Admin theme'}
              subtitle={t('admin_theme_subtitle') || 'Customize the back-office look & feel'}
            >
              <Select
                value={adminTheme.preset}
                onChange={(e) => handleAdminThemeChange({ preset: e.target.value })}
                size="small"
                fullWidth
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
              >
                {ADMIN_THEME_PRESETS.map((preset) => (
                  <MenuItem key={preset.key} value={preset.key}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>

              <ModernSwitch
                checked={adminTheme.mode === 'dark'}
                onChange={(e) => handleAdminThemeChange({ mode: e.target.checked ? 'dark' : 'light' })}
                name="adminThemeMode"
                label={t('dark_mode') || 'Dark mode'}
                isRTL={isRTL}
              />

              {adminSwatch ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  {[adminSwatch.primary, adminSwatch.accent, adminSwatch.background, adminSwatch.paper].map((color, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        height: 28,
                        borderRadius: 1.5,
                        bgcolor: color,
                        border: '1px solid rgba(15,23,42,0.12)',
                      }}
                    />
                  ))}
                </Box>
              ) : null}
            </SettingsCard>
          </Grid>

          {/* Country & Maintenance Card */}
          <Grid item xs={12} md={6} lg={4}>
            <SettingsCard
              icon={<PublicIcon />}
              iconBgColor="#DBEAFE"
              iconColor="#2563EB"
              title={t('region_settings') || 'Region & Maintenance'}
              subtitle={t('region_config') || 'Country and maintenance settings'}
            >
              <CountryListSelect
                label={t('select_country')}
                dis={true}
                countries={countries}
                onlyCode={false}
                value={countryCode ? countryCode : null}
                onChange={
                  (object, value) => {
                    handleCountryChange(value);
                  }
                }
              />
              <CountryListSelect
                label={t('country_restriction')}
                dis={true}
                countries={countries}
                onlyCode={true}
                value={country ? country : null}
                onChange={
                  (object, value) => {
                    handleCountryCode(value);
                  }
                }
              />
              <ModernSwitch
                checked={!!data.maintenanceMode}
                onChange={handleSwitchChange}
                name="maintenanceMode"
                label={t('maintenance_mode')}
                isRTL={isRTL}
              />
              <TextField
                className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                minRows={2}
                id="maintenanceMessage"
                label={t('maintenance_message')}
                name="maintenanceMessage"
                onChange={handleTextChange}
                value={data.maintenanceMessage || ''}
                disabled={!data.maintenanceMode}
              />
            </SettingsCard>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{
                  bgcolor: '#111827',
                  color: '#FFFFFF',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 14,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#374151',
                    boxShadow: 'none',
                  },
                }}
              >
                {t('save_changes') || 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <AlertDialog open={settingsdata.error.flag && clicked} onClose={handleClose}>{t('update_failed')}</AlertDialog>
      </Box>
      :
      <CircularLoading />
  );

}

export default GeneralSettings;
